<template>
  <div class="step1-form">
    <div v-if="isRejected" class="reject-alert">
      <i class="el-icon-error"></i>
      <div class="reject-alert-content">
        <div class="reject-alert-title">
          {{ rejectTitle }}
        </div>
        <div class="reject-alert-desc">{{ rejectDesc }}</div>
      </div>
    </div>
    <div class="section-header">
      <div class="blue-bar"></div>
      <span class="section-title">选择资金收款户主体</span>
    </div>

    <el-form ref="formRef" :model="formModel" :rules="rules" label-position="top" class="step-form">
      <el-row :gutter="20">
        <el-col :span="8">
          <el-form-item label="企业主体全称" prop="companyName" required>
            <el-select
              v-model="formModel.companyName"
              placeholder="请选择"
              filterable
              style="width: 100%"
              :loading="companyOptionsLoading"
              loading-text="企业主体加载中..."
              no-data-text="暂无企业主体"
              no-match-text="无匹配企业主体"
            >
              <el-option
                v-for="item in companyOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value">
              </el-option>
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="统一社会信用代码">
            <div class="readonly-text">{{ (selectedCompany && selectedCompany.creditCode) || '--' }}</div>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="法人姓名">
            <div class="readonly-text">{{ (selectedCompany && selectedCompany.legalPerson) || '--' }}</div>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="8">
          <el-form-item label="法人手机号">
            <div class="readonly-text">{{ (selectedCompany && selectedCompany.legalPhone) || '--' }}</div>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="法人身份证号">
            <div class="readonly-text">{{ (selectedCompany && selectedCompany.legalIdCard) || '--' }}</div>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="联系人姓名">
            <div class="readonly-text">{{ (selectedCompany && selectedCompany.contactName) || '--' }}</div>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="8">
          <el-form-item label="联系人电话">
            <div class="readonly-text">{{ (selectedCompany && selectedCompany.contactPhone) || '--' }}</div>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="联系地址">
            <div class="readonly-text">{{ (selectedCompany && selectedCompany.address) || '--' }}</div>
          </el-form-item>
        </el-col>
      </el-row>

      <template v-if="formModel.companyName">
        <div class="section-header" style="margin-top: 10px;">
          <div class="blue-bar"></div>
          <span class="section-title">提交进件材料</span>
        </div>

        <el-row :gutter="20">
          <el-col :span="6">
            <el-form-item label="支付通道" prop="paymentChannel" required>
              <el-select v-model="formModel.paymentChannel" placeholder="请选择" style="width: 100%">
                <el-option label="网商银行" value="网商银行"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="商户编号" prop="merchantNo" required>
              <el-input
                v-model="formModel.merchantNo"
                placeholder="请输入开头为226801000******的编号"
                @input="handleMerchantNoInput"
              ></el-input>
            </el-form-item>
          </el-col>
        </el-row>
      </template>
    </el-form>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { getMerchantList } from '@/api/domain/organize.js'

const props = defineProps({
  value: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['input', 'select-company'])

const formRef = ref(null)
const companyOptions = ref([])
const companyOptionsLoading = ref(false)

const formModel = computed({
  get: () => props.value,
  set: (val) => emit('input', val),
})

const selectedCompany = computed(() => {
  return companyOptions.value.find((item) => item.value === formModel.value.companyName) || null
})

const isRejected = computed(() => Number(formModel.value.authState) === -1)

const rejectTitle = computed(() => {
  const companyName = formModel.value.rejectCompanyName

  if (Number(formModel.value.failType) === 1) {
    return `${companyName}未与乾三签约`
  }
  return '账户开通失败'
})

const rejectDesc = computed(() => {
  return formModel.value.failReason
})

watch(selectedCompany, (val) => {
  emit('select-company', val)
  if (val && val.merchantNo) {
    formModel.value.merchantNo = val.merchantNo
  }
}, { immediate: true })

const fetchCompanyList = async (isAutoSelectLast = false) => {
  companyOptionsLoading.value = true
  try {
    const { code, data } = await getMerchantList()
    if (code === 1) {
      companyOptions.value = (data.vehicleEnterpriseEntityList || []).map((item) => ({
        label: item.corporationName,
        value: item.id,
        merchantNo: item.merchantNo,
        creditCode: item.licenceNo,
        legalPerson: item.legalName,
        legalIdNo: item.legalIdNo,
        legalMobile: item.legalMobile,
        contactName: item.contactName,
        contactMobile: item.contactMobile,
        contactAddress: item.contactAddress,
        registrationAddress: item.registrationAddress,
        legalPhone: item.legalMobile,
        legalIdCard: item.legalIdNo,
        contactPhone: item.contactMobile,
        address: item.contactAddress || item.registrationAddress,
      }))

      if (isAutoSelectLast && companyOptions.value.length > 0) {
        formModel.value.companyName = companyOptions.value[companyOptions.value.length - 1].value
      }
    }
  } finally {
    companyOptionsLoading.value = false
  }
}

onMounted(() => {
  fetchCompanyList()
})

const handleMerchantNoInput = (val) => {
  formModel.value.merchantNo = val.replace(/[^\d]/g, '')
}

const rules = {
  companyName: [
    { required: true, message: '请选择企业主体全称', trigger: 'change' },
  ],
  paymentChannel: [
    { required: true, message: '请选择支付通道', trigger: 'change' },
  ],
  merchantNo: [
    { required: true, message: '请输入商户编号', trigger: ['blur', 'change'] },
    { pattern: /^226801000/, message: '请输入开头为226801000的编号', trigger: 'blur' },
  ],
}

const validate = (callback) => {
  formRef.value.validate(callback)
}

const resetFields = () => {
  formRef.value.resetFields()
}

defineExpose({
  validate,
  resetFields,
})
</script>

<style scoped lang="scss">
.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #EBEEF5;

  .blue-bar {
    width: 4px;
    height: 16px;
    background: #409EFF;
    margin-right: 8px;
    border-radius: 2px;
  }

  .section-title {
    font-size: 16px;
    font-weight: 500;
    color: #303133;
  }
}

.reject-alert {
  display: flex;
  align-items: flex-start;
  padding: 12px 16px;
  margin-bottom: 16px;
  border-radius: 4px;
  background: #FFF3F1;
  border: 1px solid #FDE2DF;

  .el-icon-error {
    color: #F56C6C;
    font-size: 16px;
    margin-right: 10px;
    margin-top: 2px;
  }

  .reject-alert-content {
    color: #303133;
    min-width: 0;
  }

  .reject-alert-title {
    font-size: 14px;
    font-weight: 500;
    line-height: 22px;
    word-break: break-all;
  }

  .reject-alert-desc {
    margin-top: 2px;
    color: #909399;
    font-size: 12px;
    line-height: 20px;
    word-break: break-all;
  }
}

.step-form {
  ::v-deep .el-form-item .el-form-item__label {
    justify-content: flex-start;
    width: 300px;
  }
  ::v-deep .el-form-item__label {
    padding-bottom: 4px;
    color: #606266;
    font-weight: normal;
    text-align: left;
  }
}

.form-helper-text {
  font-size: 12px;
  color: #909399;
  line-height: 20px;
  margin-top: 4px;

  .link-text {
    color: #409EFF;
    cursor: pointer;
    margin-left: 4px;

    &:hover {
      text-decoration: underline;
    }
  }
}

.readonly-text {
  color: #303133;
  font-size: 14px;
}
</style>
